import intl from 'react-intl-universal';
import { useState } from 'react';
import { Row, Col, Tooltip, Image, Popover } from 'antd';
import {
    QuestionCircleOutlined,
    EyeOutlined,
    PlayCircleOutlined,
} from '@ant-design/icons';
import { isFunction, get, assignIn } from 'lodash';
import style from './index.less';
import { getFileType } from '@/utils/common';
import Annex from '@/components/Annex';
import radioIcon from '@/assets/radio-default-icon.png';
import videoIcon from '@/assets/video-default-icon.png';

export type columnItem = {
    label: string;
    key: string; //
    tooltip?: string | React.ReactNode;
    render?: (
        value?: any,
        data?: { [key: string]: any },
        configs?: columnItem,
    ) => string | React.ReactNode;
    color?: string;
    isTime?: boolean;
    isImgUrl?: boolean;
    clickText?: string;
    isValueClick?: boolean; // value是否可点击
    isHover?: boolean;
    onClickEvent?: any;
    labelLeftInfo?: any;
    peerKey?: string;
    peerColor?: string;
    unit?: string;
    peerStr?: string;
    labelWidth?: number;
    col?: number;
};

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    data: {
        [key: string]: any;
    };
    config?: {
        col?: number;
        colon?: boolean; // 是否带冒号
    };
    columns: columnItem[];
    labelWidth?: number;
}

const defaultConfigs = {
    col: 3,
    colon: true,
};

const Exhibition: React.FC<Props> = (props) => {
    let {
        config = defaultConfigs,
        columns = [],
        data = {},
        labelWidth = 110,
        ...other
    } = props;
    const [url, setUrl] = useState('');

    // 合并配置
    if (config !== defaultConfigs) {
        config = assignIn(defaultConfigs, config);
    }
    const { col = 3, colon } = config;

    const showLink = (val: string) => {
        const fileType = getFileType(val);
        return isImg(fileType, val);
    };

    const isImg = (val: string, url: string) => {
        if (val === 'image') {
            return (
                <Image
                    key={url}
                    src={url}
                    rootClassName={style.imgItem}
                    preview={{
                        mask: (
                            <span>
                                <EyeOutlined />
                            </span>
                        ),
                    }}
                />
            );
        } else if (val === 'video') {
            return (
                <div
                    key={url}
                    className={style.videoItem}
                    onClick={() => {
                        setUrl(url);
                    }}
                >
                    <video
                        src={url}
                        autoPlay={false}
                        controls={false}
                        muted
                        preload="preload"
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'cover',
                        }}
                        loop={true}
                        // poster={`${url}?x-oss-process=video/snapshot,t_10,f_jpg,w_800,h_600,m_fast`}
                        poster={videoIcon}
                    >
                        {intl.get('您的浏览器不支持')}
                    </video>
                    <div className={style.videoMask}>
                        <PlayCircleOutlined />
                    </div>
                </div>
            );
        } else if (val === 'radio') {
            return (
                <div
                    key={url}
                    className={style.videoItem}
                    onClick={() => {
                        setUrl(url);
                    }}
                >
                    <img
                        src={radioIcon}
                        alt=""
                    />
                </div>
                // <audio
                //     src={url}
                //     controls
                // >
                //     您的浏览器不支持 audio 标签。
                // </audio>
            );
        } else {
            return '-';
        }
    };

    const renderValue = (item: columnItem): React.ReactNode | string => {
        const value = get(data, item.key);
        if (item.render && isFunction(item.render))
            return item.render(value, data, item);
        if (item.isImgUrl) {
            if (Array.isArray(value)) {
                if (value.length) {
                    return (
                        <Image.PreviewGroup>
                            {value.map((item: any) => {
                                return showLink(item);
                            })}
                        </Image.PreviewGroup>
                    );
                }

                return '-';
            } else if (value) {
                return (
                    <Image.PreviewGroup> {showLink(value)}</Image.PreviewGroup>
                );
            } else {
                return '-';
            }
        }
        if (item.clickText) {
            return (
                <a onClick={() => item?.onClickEvent?.(data)}>
                    {item.clickText}
                </a>
            );
        }
        if (value || value === 0) {
            if (String(value).length >= 5) {
                return (
                    <Popover
                        content={value}
                        placement="topLeft"
                        overlayStyle={{
                            maxWidth: '400px',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            wordBreak: 'break-all',
                        }}
                    >
                        {item?.isValueClick ? (
                            <a onClick={() => item?.onClickEvent?.(data, item)}>
                                {value}
                            </a>
                        ) : (
                            <span
                                style={{
                                    color: item.color,
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {value}
                            </span>
                        )}
                    </Popover>
                );
            } else {
                return item?.isValueClick ? (
                    <a onClick={() => item?.onClickEvent?.(data, item)}>
                        {value}
                    </a>
                ) : (
                    <span
                        style={{ color: item.color, overflowWrap: 'anywhere' }}
                    >
                        {value}
                    </span>
                );
            }
        }
        return '-';
    };

    return (
        <Row
            {...other}
            className={`exhibitionContainer ${other.className}`}
        >
            {columns?.map((item: any, index) => (
                <Col
                    className={`exhibitionItem ${style.exhibitionItem}`}
                    key={index}
                    span={item?.col || 24 / col}
                >
                    {item.label && (
                        <div
                            className={`label ${style.label}`}
                            style={{ width: item.labelWidth || labelWidth }}
                        >
                            {item.label}
                            {item.tooltip && (
                                <Tooltip title={item.tooltip}>
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            )}
                            {colon && '：'}
                        </div>
                    )}
                    <div
                        className={`value ${
                            !item.isImgUrl ? style.value : style.imgContainer
                        }`}
                    >
                        {renderValue(item)}
                        {item.labelLeftInfo && (
                            <div>{item.labelLeftInfo(data)}</div>
                        )}
                    </div>
                </Col>
            ))}
            <Annex
                src={url}
                removeUrl={(val: string) => setUrl(val)}
            ></Annex>
        </Row>
    );
};

export default Exhibition;
